export type SeoGuideVisualAssetKey =
  | 'productPhotographyWorkflowOverview'
  | 'productPhotographyUploadFromDevice'
  | 'productPhotographyWorkflowInputs'
  | 'productPhotographyRequestPlanning'
  | 'productPhotographyGenerationProgress'
  | 'productPhotographyGeneratedResult'
  | 'fashionModelGarmentSource'
  | 'fashionModelReference'
  | 'fashionModelGenerationInputs'
  | 'fashionModelReviewResults'
  | 'fashionModelApprovedLaunchImage'
  | 'referenceProductSource'
  | 'referenceSceneReference'
  | 'referenceBrandChannelReference'
  | 'referenceImageDecompositionInterface'
  | 'referenceComparison1'
  | 'referenceComparison2'
  | 'referenceComparison3'
  | 'referenceComparison4'
  | 'referenceReviewInterface'
  | 'referenceStyleJacketFirst'
  | 'referenceStyleJacketRevised'
  | 'referenceStyleTrench'
  | 'lookbookCollectionBrief'
  | 'lookbookSequencePlanning'
  | 'lookbookShotList'
  | 'lookbookVisualDirection'
  | 'lookbookSequenceReview'
  | 'lookbookDigitalExperience'
  | 'imageToVideoSourceStill'
  | 'imageToVideoMotionPlanInterface'
  | 'imageToVideoFormatPlanningInterface'
  | 'imageToVideoAssetPackage'
  | 'imageToVideoFrameTestGrid'
  | 'imageToVideoFashionAdStep6';

interface SeoGuideVisualAsset {
  src: string;
  width: number;
  height: number;
  mediaType?: 'image' | 'video';
}

export interface SeoGuideVisual extends SeoGuideVisualAsset {
  alt: string;
  caption: string;
}

export const SEO_GUIDE_VISUAL_ASSETS: Record<SeoGuideVisualAssetKey, SeoGuideVisualAsset> = {
  productPhotographyWorkflowOverview: { src: '/guides/ai-product-photography/workflow-overview.webp', width: 1254, height: 1254 },
  productPhotographyUploadFromDevice: { src: '/guides/ai-product-photography/upload-from-device.webp', width: 1524, height: 724 },
  productPhotographyWorkflowInputs: { src: '/guides/ai-product-photography/workflow-inputs.webp', width: 1716, height: 1096 },
  productPhotographyRequestPlanning: { src: '/guides/ai-product-photography/request-planning.webp', width: 965, height: 1630 },
  productPhotographyGenerationProgress: { src: '/guides/ai-product-photography/generation-progress.webp', width: 738, height: 1800 },
  productPhotographyGeneratedResult: { src: '/guides/ai-product-photography/generated-result.webp', width: 1340, height: 1800 },
  fashionModelGarmentSource: { src: '/guides/ai-fashion-model/garment-source.webp', width: 1024, height: 1376 },
  fashionModelReference: { src: '/guides/ai-fashion-model/model-reference.webp', width: 896, height: 1200 },
  fashionModelGenerationInputs: { src: '/guides/ai-fashion-model/generation-inputs.webp', width: 900, height: 719 },
  fashionModelReviewResults: { src: '/guides/ai-fashion-model/review-results.webp', width: 970, height: 1621 },
  fashionModelApprovedLaunchImage: { src: '/guides/ai-fashion-model/approved-launch-image.webp', width: 1350, height: 1800 },
  referenceProductSource: { src: '/guides/reference-image/product-source.webp', width: 1254, height: 1254 },
  referenceSceneReference: { src: '/guides/reference-image/scene-reference.webp', width: 1344, height: 1800 },
  referenceBrandChannelReference: { src: '/guides/reference-image/brand-channel-reference.webp', width: 1082, height: 1454 },
  referenceImageDecompositionInterface: { src: '/guides/reference-image/decompose-visual-variables.png', width: 910, height: 1729 },
  referenceComparison1: { src: '/guides/reference-image/comparison-1.webp', width: 1339, height: 1800 },
  referenceComparison2: { src: '/guides/reference-image/comparison-2.webp', width: 1082, height: 1454 },
  referenceComparison3: { src: '/guides/reference-image/comparison-3.webp', width: 1082, height: 1454 },
  referenceComparison4: { src: '/guides/reference-image/comparison-4.webp', width: 1081, height: 1455 },
  referenceReviewInterface: { src: '/guides/reference-image/review-interface.webp', width: 767, height: 1800 },
  referenceStyleJacketFirst: { src: '/guides/reference-image/style-case-20260913/jacket-first.webp', width: 1024, height: 1536 },
  referenceStyleJacketRevised: { src: '/guides/reference-image/style-case-20260913/jacket-verified.webp', width: 1024, height: 1536 },
  referenceStyleTrench: { src: '/guides/reference-image/style-case-20260913/trench-same-style.webp', width: 1024, height: 1536 },
  lookbookCollectionBrief: { src: '/guides/ai-lookbook/collection-brief.webp', width: 1448, height: 1086 },
  lookbookSequencePlanning: { src: '/guides/ai-lookbook/sequence-planning.webp', width: 1800, height: 683 },
  lookbookShotList: { src: '/guides/ai-lookbook/shot-list.webp', width: 1536, height: 1024 },
  lookbookVisualDirection: { src: '/guides/ai-lookbook/visual-direction.webp', width: 1448, height: 1086 },
  lookbookSequenceReview: { src: '/guides/ai-lookbook/sequence-review.webp', width: 760, height: 1800 },
  lookbookDigitalExperience: { src: '/guides/ai-lookbook/digital-experience.webp', width: 1344, height: 1800 },
  imageToVideoSourceStill: { src: '/guides/image-to-video/source-still.webp', width: 1344, height: 1800 },
  imageToVideoMotionPlanInterface: { src: '/guides/image-to-video/motion-plan-interface.webp', width: 1372, height: 770 },
  imageToVideoFormatPlanningInterface: { src: '/guides/image-to-video/format-planning-interface.webp', width: 1364, height: 250 },
  imageToVideoAssetPackage: { src: '/guides/image-to-video/asset-package-versions.png', width: 1600, height: 1000 },
  imageToVideoFrameTestGrid: { src: '/guides/image-to-video/frame-test-grid.webp', width: 1470, height: 1080 },
  imageToVideoFashionAdStep6: { src: '/guides/image-to-video/fashion-ad-step-6.mp4', width: 1244, height: 1664, mediaType: 'video' },
};

export function guideVisual(key: SeoGuideVisualAssetKey, alt: string, caption: string): SeoGuideVisual {
  return { ...SEO_GUIDE_VISUAL_ASSETS[key], alt, caption };
}
